/* eslint-disable no-unused-vars */
import { useState, useContext, useEffect } from "react";
import { AdminContext } from "../../../context/AdminContext";
import {
  Plus,
  Edit3,
  Trash2,
  X,
  Check,
  GripVertical,
  Tag,
  AlertTriangle,
} from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";

const CategoryManager = () => {
  const {
    blogCategories,
    fetchBlogCategories,
    createBlogCategory,
    updateBlogCategory,
    deleteBlogCategory,
  } = useContext(AdminContext);
  const { isDarkMode } = useTheme();

  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    nameEn: "",
    nameAm: "",
    descriptionEn: "",
    descriptionAm: "",
    color: "#CE257A",
  });

  const presetColors = [
    "#CE257A",
    "#3B82F6",
    "#10B981",
    "#F59E0B",
    "#8B5CF6",
    "#EF4444",
    "#06B6D4",
    "#EC4899",
    "#14B8A6",
    "#F97316",
  ];

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    await fetchBlogCategories();
    setLoading(false);
  };

  const resetForm = () => {
    setForm({
      nameEn: "",
      nameAm: "",
      descriptionEn: "",
      descriptionAm: "",
      color: "#CE257A",
    });
    setShowForm(false);
    setEditingId(null);
  };

  const handleEdit = (cat) => {
    setForm({
      nameEn: cat.nameEn || "",
      nameAm: cat.nameAm || "",
      descriptionEn: cat.descriptionEn || "",
      descriptionAm: cat.descriptionAm || "",
      color: cat.color || "#CE257A",
    });
    setEditingId(cat.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nameEn.trim()) return alert("English name is required");

    if (editingId) {
      await updateBlogCategory(editingId, form);
    } else {
      await createBlogCategory(form);
    }
    resetForm();
    await fetchBlogCategories();
  };

  const handleDelete = async (cat) => {
    if (cat.blogCount > 0) {
      return alert(
        `Cannot delete: ${cat.blogCount} blog(s) reference this category. Reassign or delete them first.`,
      );
    }
    if (!confirm(`Delete category "${cat.nameEn}"?`)) return;
    await deleteBlogCategory(cat.id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text dark:text-dark-text">
            Blog Categories
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1 text-sm">
            Organize blog content by categories.
            {blogCategories.length > 0 && (
              <span className="ml-2 font-semibold text-primary">
                ({blogCategories.length} total)
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl text-sm font-bold transition-colors shadow-lg shadow-primary/20"
        >
          <Plus size={18} /> Add
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-text dark:text-dark-text">
              {editingId ? "Edit Category" : "New Category"}
            </h3>
            <button
              onClick={resetForm}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={18} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-text dark:text-dark-text mb-1">
                  Name (English) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.nameEn}
                  onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
                  placeholder="e.g. Parenting Tips"
                  className="w-full px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-400 font-medium"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-text dark:text-dark-text mb-1">
                  Name (አማርኛ)
                </label>
                <input
                  type="text"
                  value={form.nameAm}
                  onChange={(e) => setForm({ ...form, nameAm: e.target.value })}
                  placeholder="e.g. የወላጅ ምክሮች"
                  className="w-full px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-400 font-medium"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-text dark:text-dark-text mb-1">
                  Description (English)
                </label>
                <textarea
                  value={form.descriptionEn}
                  onChange={(e) =>
                    setForm({ ...form, descriptionEn: e.target.value })
                  }
                  placeholder="Brief description of this category..."
                  rows={3}
                  className="w-full px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-400 font-medium resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-text dark:text-dark-text mb-1">
                  Description (አማርኛ)
                </label>
                <textarea
                  value={form.descriptionAm}
                  onChange={(e) =>
                    setForm({ ...form, descriptionAm: e.target.value })
                  }
                  placeholder="የዚህ ምድብ አጭር መግለጫ..."
                  rows={3}
                  className="w-full px-4 py-3 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-400 font-medium resize-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-text dark:text-dark-text mb-1">
                Color
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                {presetColors.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, color: c })}
                    className={`w-8 h-8 rounded-full border-2 transition-transform ${
                      form.color === c
                        ? "border-text dark:border-dark-text scale-110"
                        : "border-transparent"
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={resetForm}
                className="px-5 py-2.5 border border-gray-200 dark:border-dark-border text-gray-600 dark:text-dark-muted rounded-xl font-bold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold transition-colors shadow-lg shadow-primary/20"
              >
                <Check size={16} className="inline mr-1" />
                {editingId ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Category List */}
      <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border overflow-hidden">
        {blogCategories.length === 0 ? (
          <div className="text-center py-16">
            <Tag className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-text dark:text-dark-text mb-2">
              No categories yet
            </h3>
            <p className="text-gray-500 dark:text-dark-muted text-sm">
              Create your first category to organize blog posts.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-dark-border/50">
            {blogCategories.map((cat) => (
              <div
                key={cat.id}
                className="flex items-center gap-4 p-4 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors"
              >
                <div className="flex items-center gap-1 text-gray-300 dark:text-gray-600">
                  <GripVertical size={16} />
                </div>
                <div
                  className="w-4 h-4 rounded-full flex-shrink-0"
                  style={{ backgroundColor: cat.color }}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-text dark:text-dark-text">
                    {cat.nameEn || "Untitled"}
                  </p>
                  {cat.nameAm && (
                    <p className="text-xs text-gray-400 dark:text-dark-muted">
                      {cat.nameAm}
                    </p>
                  )}
                  {cat.descriptionEn && (
                    <p className="text-xs text-gray-500 dark:text-dark-muted mt-1 line-clamp-1">
                      {cat.descriptionEn}
                    </p>
                  )}
                </div>
                <span className="text-xs text-gray-400 font-medium px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  {cat.blogCount || 0} blogs
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleEdit(cat)}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-primary transition-colors"
                  >
                    <Edit3 size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(cat)}
                    className={`p-2 rounded-lg transition-colors ${
                      cat.blogCount > 0
                        ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                        : "text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    }`}
                    title={
                      cat.blogCount > 0
                        ? `${cat.blogCount} blog(s) use this category`
                        : "Delete"
                    }
                  >
                    {cat.blogCount > 0 ? (
                      <AlertTriangle size={14} />
                    ) : (
                      <Trash2 size={14} />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryManager;
